import axios, { AxiosResponse } from "axios";
import { InstitutionUrl } from "./AUrl";

/*
The interface for the response from the get available licenses endpoint.

@id: The license ID.
@name: The name of the license.
@license_key: The license key.
*/
export interface License {
    id: number;
    name: string;
    license_key: string;
    available: boolean;
    // The email of the user this license is currently assigned to, or null when
    // it is unassigned. `available` is a static flag on the license and can be
    // true even when it is already assigned, so this is what tells us whether a
    // license is free to hand out.
    assigned_to?: string | null;
}

/*
The interface for the API response.

@status: The HTTP status code.
@error: The error message if any.
@licenses: The list of available licenses if applicable.
*/
export interface GetLicensesResponse {
    status: number;
    error?: string;
    licenses?: License[];
}

// Function Signature
export type GetLicensesType = (
    jwt: string,
    institutionId: number,
    departmentId?: number,
) => Promise<GetLicensesResponse>;

/*
The function to get available licenses for an institution (and optionally a department).

@jwt: The JWT of the user making the request.
@institutionId: The ID of the institution.
@departmentId: (Optional) The ID of the department.

@returns: The ApiResponse from the get licenses endpoint.
*/
export const getLicenses: GetLicensesType = async (
    jwt,
    institutionId,
    departmentId,
) => {
    const url = new InstitutionUrl().get_available_licenses;
    if (!jwt) {
        return { status: 401, error: "JWT has expired" };
    }

    let response: AxiosResponse<any>;
    try {
        response = await axios.post<GetLicensesResponse | string>(
            url,
            {
                institution_id: institutionId,
                ...(departmentId && { department_id: departmentId }),
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    token: jwt,
                },
                validateStatus: () => true, // Resolve promise for all HTTP status codes
            },
        );
    } catch (error) {
        return {
            status: 500,
            error: "Network or other error occurred",
        };
    }

    return response.status === 200
        ? { status: response.status, licenses: response.data as License[] }
        : { status: response.status, error: response.data as string };
};
